/* Fenn (19/09/2026, pedido do Leonardo): botões de trocar tamanho/cor direto na linha do
 * carrinho, na gaveta (ver snippets/cart-products.liquid, .cart-items__troca-variante).
 *
 * O Shopify não tem uma chamada pra "trocar a variante" de uma linha existente - uma linha do
 * carrinho é sempre presa a UMA variante. Trocar de tamanho/cor é, por baixo, duas chamadas:
 * 1) /cart/add.js com a variante NOVA (mesma quantidade da linha antiga);
 * 2) /cart/change.js zerando a linha ANTIGA (mesmo padrão de component-cart-items.js
 *    updateQuantity, usando a posição "line", não o id da variante).
 * A ordem das linhas já existentes nunca muda nessas duas chamadas (o Shopify sempre acrescenta
 * item novo no fim, ou soma na linha existente se a variante nova já estiver no carrinho em
 * outra linha) - por isso a posição "line" da linha antiga, lida uma vez no início, continua
 * válida pra zerar ela depois de adicionar a nova.
 *
 * Escuta 'change' em todo <select data-fenn-troca-select> por delegação (document), porque a
 * gaveta é remontada (morph) a cada atualização do carrinho - um listener preso num elemento
 * específico sumiria no primeiro morph.
 */
import { fetchConfig } from '@theme/utilities';
import { morphSection } from '@theme/section-renderer';

/** @param {Event} event */
async function aoTrocarSelect(event) {
  const select = event.target;
  if (!(select instanceof HTMLSelectElement) || !select.matches('[data-fenn-troca-select]')) return;

  const container = select.closest('[data-fenn-troca-variante]');
  if (!(container instanceof HTMLElement)) return;

  const linha = Number(container.dataset.line);
  const nomesOpcoes = JSON.parse(container.dataset.optionNames || '[]');
  const valoresAtuais = JSON.parse(container.dataset.currentOptions || '[]');
  const mapaEl = container.querySelector('[data-fenn-troca-mapa]');
  if (!linha || !mapaEl?.textContent) return;

  const mapa = JSON.parse(mapaEl.textContent);
  const nomeOpcaoTrocada = select.dataset.optionName;
  const indice = nomesOpcoes.indexOf(nomeOpcaoTrocada);
  if (indice === -1) return;

  const novosValores = [...valoresAtuais];
  novosValores[indice] = select.value;

  const alvo = mapa.find(
    (/** @type {{id: number, options: string[]}} */ v) =>
      v.options.length === novosValores.length && v.options.every((o, i) => o === novosValores[i])
  );
  if (!alvo) return; // combinação sem variante (não devia acontecer com opções válidas)

  const linha_tr = select.closest('tr[role="row"]');
  const quantidadeInput = linha_tr?.querySelector('input[data-cart-quantity]');
  const quantidade = quantidadeInput instanceof HTMLInputElement ? Number(quantidadeInput.value) || 1 : 1;

  const cartItemsComponent = document.querySelector('cart-items-component');
  const sectionId = cartItemsComponent instanceof HTMLElement ? cartItemsComponent.dataset.sectionId : null;
  if (!sectionId) return;

  container.setAttribute('data-fenn-troca-carregando', '');

  try {
    const respostaAdd = await fetch(
      Theme.routes.cart_add_url,
      fetchConfig('json', {
        body: JSON.stringify({
          items: [{ id: alvo.id, quantity: quantidade }],
          sections: sectionId,
        }),
      })
    ).then((r) => r.json());

    if (respostaAdd.status) {
      console.error('[fenn-cart-troca-variante] Falha ao adicionar variante nova:', respostaAdd);
      return;
    }

    const respostaRemove = await fetch(
      Theme.routes.cart_change_url,
      fetchConfig('json', {
        body: JSON.stringify({
          line: linha,
          quantity: 0,
          sections: sectionId,
        }),
      })
    ).then((r) => r.json());

    if (respostaRemove.errors) {
      console.error('[fenn-cart-troca-variante] Falha ao remover a linha antiga:', respostaRemove);
      return;
    }

    const html = respostaRemove.sections?.[sectionId];
    if (html) {
      await morphSection(sectionId, html, { mode: 'hydration' });
    }
  } catch (erro) {
    console.error('[fenn-cart-troca-variante] Erro trocando variante:', erro);
  }
}

document.addEventListener('change', aoTrocarSelect);
